import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  Autocomplete,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  CircularProgress,
  Chip,
  DialogActions,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAppDispatch } from '../../store/hooks';
import { fetchLedgers } from '../../store/coaStore/coaThunk';
import { ILedger } from '../../utils/coa.types';
import axios from '../../utils/axios';
import { toast } from 'react-toastify';
import { recordLedgerAdjustment } from '../../store/ledgerAdjustmentStore/ledgerAdjustmentThunk';

interface DialogAddLedgerEntryProps {
  open: boolean;
  onClose: (refresh?: boolean) => void;
}

const DialogAddLedgerEntry: FC<DialogAddLedgerEntryProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();

  const [fromLedger, setFromLedger] = useState<ILedger | null>(null);
  const [toLedger, setToLedger] = useState<ILedger | null>(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [entryDate, setEntryDate] = useState<Date>(new Date());
  const [loading, setLoading] = useState(false);
  const [ledgerSearch, setLedgerSearch] = useState('');
  const [ledgerOptions, setLedgerOptions] = useState<ILedger[]>([]);
  const [loadingLedgers, setLoadingLedgers] = useState(false);

  const [errors, setErrors] = useState<{ fromLedger?: string; toLedger?: string; amount?: string; reason?: string }>({});

  // Fetch ledgers directly via axios
  useEffect(() => {
    if (!open) return;
    setLoadingLedgers(true);
    axios.get(`/api/v1/coa/ledgers?page=0&limit=100&search=${encodeURIComponent(ledgerSearch)}`)
      .then((res: any) => {
        setLedgerOptions(res?.data?.data || res?.data?.ledgers || []);
      })
      .catch((err) => {
        console.error('Error fetching ledgers', err);
        setLedgerOptions([]);
      })
      .finally(() => setLoadingLedgers(false));
  }, [open, ledgerSearch]);

  const validate = () => {
    const errs: typeof errors = {};
    if (!fromLedger) errs.fromLedger = 'Please select a Source account';
    if (!toLedger) errs.toLedger = 'Please select a Destination account';
    if (fromLedger && toLedger && fromLedger._id === toLedger._id) {
       errs.toLedger = 'Source and Destination accounts cannot be the same';
    }
    if (!amount || parseFloat(amount) <= 0) errs.amount = 'Amount must be greater than 0';
    if (!reason.trim()) errs.reason = 'Reason / narration is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      await dispatch(recordLedgerAdjustment({
        fromLedgerId: fromLedger?._id || '',
        toLedgerId: toLedger?._id || '',
        amount: parseFloat(amount),
        reason: reason.trim(),
        entryDate: entryDate?.toISOString(),
      })).unwrap();
      
      toast.success('Journal entry recorded successfully.');
      handleReset();
      onClose(true);
    } catch (err: any) {
      toast.error(err || 'Failed to record entry');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFromLedger(null);
    setToLedger(null);
    setAmount('');
    setReason('');
    setEntryDate(new Date());
    setErrors({});
    setLedgerSearch('');
  };

  const handleClose = () => {
    handleReset();
    onClose(false);
  };

  return (
    <div className="drawer-form-container" style={{ width: '100%', maxWidth: '700px' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', padding: '2px 0 0 2px', position: 'relative' }}>

        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '40px' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
              Add Journal Entry
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ padding: '24px', paddingBottom: '100px', overflowY: 'auto', flex: 1 }}>
          <Grid container spacing={3}>

            {/* From Account (Credit) */}
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, mb: 1, color: '#c62828' }}>
                From Account (Source / Credit) *
              </Typography>
              <Autocomplete
                options={ledgerOptions}
                loading={loadingLedgers}
                value={fromLedger}
                onChange={(_e, val) => {
                  setFromLedger(val);
                  if (val) setErrors((p) => ({ ...p, fromLedger: undefined }));
                }}
                onInputChange={(_e, val) => setLedgerSearch(val)}
                getOptionLabel={(opt) => `${opt.ledgerName || opt.name || ''} (${opt.accountingCode || ''})`}
                isOptionEqualToValue={(opt, val) => opt._id === val._id}
                renderOption={(props, option) => (
                  <li {...props} key={option._id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', py: 0.5 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                        {option.ledgerName || option.name} ({option.accountingCode})
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {(option.groupCategory as any)?.name && (
                          <Chip
                            label={(option.groupCategory as any).name}
                            size="small"
                            sx={{ fontSize: 9, height: 18, bgcolor: '#fff0f6', color: '#c2185b', fontWeight: 600 }}
                          />
                        )}
                        {(option.primaryGroup as any)?.name && (
                          <Chip
                            label={(option.primaryGroup as any).name}
                            size="small"
                            sx={{ fontSize: 9, height: 18, bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600 }}
                          />
                        )}
                      </Box>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Source Ledger"
                    size="small"
                    error={!!errors.fromLedger}
                    helperText={errors.fromLedger}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 2 }}>
                          {fromLedger?.groupCategory && (
                            <Chip
                              label={(fromLedger.groupCategory as any).name}
                              size="small"
                              sx={{ fontSize: 9, height: 18, bgcolor: '#fff0f6', color: '#c2185b', fontWeight: 600 }}
                            />
                          )}
                          {fromLedger?.primaryGroup && (
                            <Chip
                              label={(fromLedger.primaryGroup as any).name}
                              size="small"
                              sx={{ fontSize: 9, height: 18, bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600 }}
                            />
                          )}
                          {params.InputProps.endAdornment}
                        </Box>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* To Account (Debit) */}
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, mb: 1, color: '#2e7d32' }}>
                To Account (Destination / Debit) *
              </Typography>
              <Autocomplete
                options={ledgerOptions}
                loading={loadingLedgers}
                value={toLedger}
                onChange={(_e, val) => {
                  setToLedger(val);
                  if (val) setErrors((p) => ({ ...p, toLedger: undefined }));
                }}
                onInputChange={(_e, val) => setLedgerSearch(val)}
                getOptionLabel={(opt) => `${opt.ledgerName || opt.name || ''} (${opt.accountingCode || ''})`}
                isOptionEqualToValue={(opt, val) => opt._id === val._id}
                renderOption={(props, option) => (
                  <li {...props} key={option._id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', py: 0.5 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                        {option.ledgerName || option.name} ({option.accountingCode})
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {(option.groupCategory as any)?.name && (
                          <Chip
                            label={(option.groupCategory as any).name}
                            size="small"
                            sx={{ fontSize: 9, height: 18, bgcolor: '#fff0f6', color: '#c2185b', fontWeight: 600 }}
                          />
                        )}
                        {(option.primaryGroup as any)?.name && (
                          <Chip
                            label={(option.primaryGroup as any).name}
                            size="small"
                            sx={{ fontSize: 9, height: 18, bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600 }}
                          />
                        )}
                      </Box>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Destination Ledger"
                    size="small"
                    error={!!errors.toLedger}
                    helperText={errors.toLedger}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 2 }}>
                          {toLedger?.groupCategory && (
                            <Chip
                              label={(toLedger.groupCategory as any).name}
                              size="small"
                              sx={{ fontSize: 9, height: 18, bgcolor: '#fff0f6', color: '#c2185b', fontWeight: 600 }}
                            />
                          )}
                          {toLedger?.primaryGroup && (
                            <Chip
                              label={(toLedger.primaryGroup as any).name}
                              size="small"
                              sx={{ fontSize: 9, height: 18, bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600 }}
                            />
                          )}
                          {params.InputProps.endAdornment}
                        </Box>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* Amount and Date */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Amount *"
                fullWidth
                size="small"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                error={!!errors.amount}
                helperText={errors.amount}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DesktopDatePicker
                  label="Date *"
                  value={entryDate}
                  onChange={(val) => { if (val) setEntryDate(val); }}
                  renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                />
              </LocalizationProvider>
            </Grid>

            {/* Narration */}
            <Grid item xs={12}>
              <TextField
                label="Narration *"
                fullWidth
                size="small"
                multiline
                rows={2}
                placeholder="Enter narration for this journal entry..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                error={!!errors.reason}
                helperText={errors.reason}
              />
            </Grid>

            {/* Accounting Guide */}
            <Grid item xs={12}>
              <Box sx={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', p: 1.5 }}>
                <Typography sx={{ fontSize: '0.8rem', color: '#64748b', lineHeight: 1.6 }}>
                  <span style={{ fontWeight: 600, color: '#475569' }}>Double Entry Accounting Concept:</span><br />
                  • <b>From Account</b> is the Source (Value flows out). It is <b>Credited (Cr).</b><br />
                  • <b>To Account</b> is the Destination (Value flows in). It is <b>Debited (Dr).</b><br />
                  • <b>Example:</b> To record money moving from Bank to Cash, "From" is Bank and "To" is Cash.
                </Typography>
              </Box>
            </Grid>

          </Grid>
        </DialogContent>

        <DialogActions sx={{ padding: '16px 24px', borderTop: '1px solid #c1c5cb' }}>
          <Button variant='outlined' onClick={handleClose}>Cancel</Button>
          <LoadingButton onClick={handleSubmit} variant="contained" loading={loading} sx={{ minWidth: '160px' }}>
            Save Entry
          </LoadingButton>
        </DialogActions>
      </Box>
    </div>
  );
};

export default DialogAddLedgerEntry;
